import React, { useEffect } from 'react';
import T from 'prop-types';
// redux
import { connect } from 'react-redux';
import { setBreadcrumbs } from 'reducers/breadcrumbs';
import { setPageOptions } from 'reducers/layout';
import { setTableParams } from 'reducers/tableParams';
import { openPortalRight } from 'reducers/rightToLeftPortal';
import { openModal } from 'reducers/modals';
import { createUserFilterAction, updateUserFilterAction } from 'reducers/userFilters';
// components
import NothingBox from 'components/NothingBox';
import Panel from 'components/Panel';
import SearchBox from 'components/SearchBox';
import Preloader from 'components/Preloader';
import PaginationWrapper from 'components/PaginationWrapper';
import FarmRow from './components/FarmRow';
import ExportCSVButton from 'components/ExportCSVButton';
import PigGroupsExportModal from 'components/PigGroupsExportModal';
import ReportCSVExportModal from 'components/ReportCSVExportModal';
import { FormattedMessage, useIntl } from 'react-intl';
import UserFilter from 'components/UserFilter/UserFilter';
import OfflineContainer from 'components/OfflineScreen/OfflineContainer';
import UserFilterMobileHeader from 'components/UserFilterMobile/UserFilterMobileHeader';
import UserFilterMobile from 'components/UserFilterMobile/UserFilterMobile';
// api
import { applySvrFarmsFilter, downloadCorrectiveActionsCSV, downloadReportCSV, getSVRsFarms } from 'endpoints/svr';
// utils
import download from 'downloadjs';
import { toastResponseErrors } from 'utils/responseErrorsHelper';
import { getCorrectiveActionsCsvName, getReportCsvName } from 'utils/SVRHelper';
// hooks
import useDataTable, { tableParamsSelector } from 'hooks/useDataTable';
import useDidMountEffect from 'hooks/useDidMountEffect';
// utils
import { useMediaQuery } from 'react-responsive';
import { userFilterGroupNames, userFilterSectionTypes, allOrAnyFarmsOptions } from 'utils/constants/userFilters';
import { tableNames } from 'utils/constants';
import cn from 'classnames';
// styles
import './ReportFarms.scss';

const ReportFarms = ({
  isOnline,
  isSVREnabled,
  tableParams,
  setBreadcrumbs,
  setPageOptions,
  router,
  setTableParams,
  openPortalRight,
  openModal,
  createUserFilterAction,
  updateUserFilterAction,
}) => {
  const { formatMessage } = useIntl();
  const sections = [
    { value: userFilterSectionTypes.farm, label: formatMessage({ id: 'general.farmName' }) },
    { value: userFilterSectionTypes.managementCompany, label: formatMessage({ id: 'general.role.managementCo' }) },
    { value: userFilterSectionTypes.latestSvrCreatedAt, label: formatMessage({ id: 'general.latestReportDate' }) },
    { value: userFilterSectionTypes.latestSvrCreatedBy, label: formatMessage({ id: 'general.latestReportAuthor' }) },
  ];
  const isDesktop = useMediaQuery({ minWidth: 1024 });
  const {
    data: { resources, isLoading, meta },
    fetchData,
    onPageChange,
    onPerPageChange,
    onSearchChange,
    onUserFilterSectionsChange,
    onUserFilterSegmentSave,
  } = useDataTable(getSVRsFarms, {
    setTableParams: (params) => setTableParams(tableNames.reportFarms, params),
    tableParams,
    filterFetcher: applySvrFarmsFilter,
    userFilterGroupName: userFilterGroupNames.svrsFarms,
    createUserFilter: createUserFilterAction,
    updateUserFilter: updateUserFilterAction,
  });

  const downloadCSV = (params) => {
    return downloadCorrectiveActionsCSV(params)
      .then((blob) => download(blob, getCorrectiveActionsCsvName(), 'text/csv'))
      .catch(toastResponseErrors);
  };

  const handleExportCSV = () => {
    openModal(
      <PigGroupsExportModal
        onSubmit={downloadCSV}
        title={<FormattedMessage id="general.exportCorrectiveActionsCSV" />}
      />
    );
  };

  const downloadReportsCSV = (params) => {
    return downloadReportCSV(params)
      .then((blob) => download(
        blob,
        getReportCsvName({ templateId: params.svr_template_id, farmId: params.farm_id }),
        'text/csv'
      ))
      .catch(toastResponseErrors);
  };

  const handleExportReportCSV = () => {
    openModal(
      <ReportCSVExportModal
        onSubmit={downloadReportsCSV}
        title={<FormattedMessage id="general.exportReportCSV" />}
      />
    );
  };

  const handleOpenMobileFilters = () => {
    openPortalRight(
      <UserFilterMobile
        allOrAnyConditionLabel={<FormattedMessage id="component.advancedSearchItem.allFarmsFilters" />}
        appliedFilter={tableParams.filters?.[0] || {}}
        filterSections={sections}
        filterGroupName={userFilterGroupNames.svrsFarms}
        setFilter={(filter) => fetchData({ ...tableParams, filters: filter ? [filter] : undefined })}
      />,
    );
  };

  const handleSetPageOptions = () => {
    setPageOptions({
      rightButton: !isDesktop && isOnline ? {
        icon: 'fa fa-filter',
        onClick: handleOpenMobileFilters,
      } : '',
    });
  };

  useEffect(() => {
    if (!isSVREnabled) {
      router.goBack();
    } else {
      fetchData({ ...tableParams, page: 1 }).catch(toastResponseErrors);
    }
  }, [isOnline]);

  useEffect(() => {
    setPageOptions({ backLink: '' });
    setBreadcrumbs([
      { label: <FormattedMessage id="general.reports" />, useLabelAsMobileTitle: true },
    ]);
    handleSetPageOptions();

    return () => {
      setPageOptions({ rightButton: '' });
    };
  }, []);

  useDidMountEffect(handleSetPageOptions, [tableParams, isDesktop, isOnline]);

  const userFilter = tableParams.filters?.[0];
  const filterSectionsCount = userFilter?.sections?.length;
  const { page, per_page, search } = tableParams;
  const { total } = meta;
  const hasMobileFilterHeader = !isDesktop && filterSectionsCount > 0;

  return (
    <div className={cn('ReportFarms row centered', { 'has-mobile-filters': hasMobileFilterHeader })}>
      <OfflineContainer>
        {!isDesktop && filterSectionsCount > 0 && (
          <UserFilterMobileHeader
            onClick={handleOpenMobileFilters}
            onClose={() => fetchData({ ...tableParams, filters: undefined })}
            sectionsCount={filterSectionsCount}
            segmentName={userFilter?.name}
          />
        )}

        {isDesktop && (
          <UserFilter
            className="filters"
            isVisible
            filterGroupName={userFilterGroupNames.svrsFarms}
            availFilterSections={sections}
            listOptions={allOrAnyFarmsOptions}
            onSectionsChange={onUserFilterSectionsChange}
            initialFilter={tableParams.filters?.[0]}
            onSave={onUserFilterSegmentSave}
          />
        )}
        <Panel className="farms-panel">
          <Panel.Heading
            className="farms-panel-title"
            title={<span className="lighter"><FormattedMessage id="general.farms" /></span>}
          >
            <Panel.Control>
              {isOnline && (
                <>
                  <ExportCSVButton className="mr-10" onClick={handleExportReportCSV}>
                    <FormattedMessage id="general.exportReportCSV" />
                  </ExportCSVButton>
                  <ExportCSVButton className="mr-10" onClick={handleExportCSV}>
                    <FormattedMessage id="general.exportCorrectiveActionsCSV" />
                  </ExportCSVButton>
                </>
              )}
              <SearchBox onChange={onSearchChange} initialValue={search} disableFocus />
            </Panel.Control>
          </Panel.Heading>
          <Panel.Body className="farms-panel-body">
            <Preloader isActive={isLoading} />

            {resources.map((farm) => (
              <FarmRow
                key={farm.id}
                onSelect={() => router.push(`/report-farms/${farm.id}/report-templates`)}
                farm={farm}
              />
            ))}

            <NothingBox
              itemsName="farms"
              display={!resources.length}
              isLoading={isLoading}
              search={search}
            >
              <FormattedMessage tagName="h1" id="component.nothingBox.noFarmsToDisplay" />
            </NothingBox>

            <PaginationWrapper
              currentPage={page}
              perPage={per_page}
              onPageChange={onPageChange}
              onPerPageChange={onPerPageChange}
              totalItems={total}
              onlyBottom
            />
          </Panel.Body>
        </Panel>
      </OfflineContainer>
    </div>
  );
};

ReportFarms.propTypes = {
  setBreadcrumbs: T.func.isRequired,
  setPageOptions: T.func.isRequired,
  openPortalRight: T.func.isRequired,
  openModal: T.func.isRequired,
  tableParams: T.object.isRequired,
  isSVREnabled: T.bool.isRequired,
  isOnline: T.bool.isRequired,
  router: T.object.isRequired,
  setTableParams: T.func.isRequired,
  createUserFilterAction: T.func.isRequired,
  updateUserFilterAction: T.func.isRequired,
};

export default connect(
  (state) => ({
    tableParams: tableParamsSelector(state, tableNames.reportFarms),
    isSVREnabled: state.auth.user.current_company.admin_svrs,
    isOnline: state.network.isOnline,
  }), {
    setPageOptions,
    setBreadcrumbs,
    setTableParams,
    openPortalRight,
    openModal,
    createUserFilterAction,
    updateUserFilterAction,
  }
)(ReportFarms);
